import Api from '@/util/Api';

/**
 * ROBO-865 — a checkout pass (ROBO-476) for an ANONYMOUS caller of the `checkout/*` routes, the React
 * twin of legacy `cartPass` in `View/js/cart-weather.js`.
 *
 * The pass comes from the `verify.hd.pics` Turnstile frame: embed
 * `?action=checkout&origin=<this page's origin>`, wait for its `ready`, answer `init` with a request id,
 * take the `token` it posts back (postMessage protocol `hdph-verify` v1), and exchange the token at
 * `POST checkout/session` for `{ pass, expiresAt }`. The pass is bound to the brand of the host it was
 * minted for — named in `hd-brand-host`, as every anonymous checkout call from the UX does — lasts
 * 60 minutes and is never extended, so it is renewed a minute before it ends.
 *
 * Two modes:
 * - **Silent** (no `challengeHost`): a challenge that wants the visitor to interact is abandoned rather
 *   than shown, and a failure is final for the page load (one attempt, no retry storm). For optional
 *   callers, such as the reschedule page's weather, a hint that must never stop anyone.
 * - **Interactive** (UX-949, `challengeHost`): for a caller that cannot complete without a pass, such as
 *   the delivery Purchase Drawer. The frame lives inside `challengeHost`, and a challenge that wants the
 *   visitor is shown there instead of abandoned. An earlier silent failure does not stop it, and its own
 *   failure is not final: the next call tries again.
 */

const FRAME_ORIGIN = 'https://verify.hd.pics';
const SOURCE = 'hdph-verify';
const VERSION = 1;

export const CHECKOUT_SESSION_PATH = 'checkout/session';

/** How long to wait for a token before giving up. Turnstile's own timeout is longer. */
export const PASS_TIMEOUT_MS = 30_000;

/** Renew this long before the pass's fixed expiry. */
const RENEW_BEFORE_MS = 60_000;

/** Once a challenge is on screen, how long the visitor has to answer it. */
export const CHALLENGE_TIMEOUT_MS = 5 * 60_000;

export interface CheckoutPassOptions {
    /** Interactive mode: where the Turnstile frame lives, and where a challenge is shown. */
    challengeHost?: HTMLElement | null;
}

interface CheckoutSession {
    pass?: string;
    expiresIn?: number;
    expiresAt?: string;
}

interface FrameMessage {
    source?: unknown;
    v?: unknown;
    type?: unknown;
    requestId?: unknown;
    token?: unknown;
    visible?: unknown;
}

let current: { pass: string; expiresAt: number } | null = null;
let pending: Promise<string> | null = null;
let failure: Error | null = null;

/** The headers an anonymous checkout call names its brand with: the host this page is served on. */
export function brandHostHeaders(): Record<string, string> {
    return { 'hd-brand-host': window.location.hostname };
}

/**
 * A pass for this page's brand: the held one while it has more than a minute left, else a new one.
 * Concurrent callers share one mint. Rejects — once per page load — when no pass can be had.
 */
export function getCheckoutPass(options: CheckoutPassOptions = {}): Promise<string> {
    const host = options.challengeHost ?? null;
    if (current && Date.now() < current.expiresAt - RENEW_BEFORE_MS)
        return Promise.resolve(current.pass);
    if (pending) return pending;
    if (failure && !host) return Promise.reject(failure);
    pending = mint(host)
        .then(minted => {
            current = minted;
            failure = null;
            return minted.pass;
        })
        .catch((error: unknown) => {
            const err = error instanceof Error ? error : new Error(String(error));
            // Only a silent failure is final; an interactive caller may try again.
            if (!host) failure = err;
            throw err;
        })
        .finally(() => {
            pending = null;
        });
    return pending;
}

/** Tests only: forget the held pass, any failure and any mint in flight. */
export function resetCheckoutPassForTests() {
    current = null;
    pending = null;
    failure = null;
}

const HIDDEN_FRAME_STYLE = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    border: '0',
    left: '-9999px',
    top: '-9999px',
    opacity: '0'
};

// Turnstile's interactive widget is 300 × 65.
const SHOWN_FRAME_STYLE = {
    position: 'static',
    width: '300px',
    height: '65px',
    border: '0',
    left: '',
    top: '',
    opacity: '1'
};

function showFrame(frame: HTMLIFrameElement, shown: boolean) {
    Object.assign(frame.style, shown ? SHOWN_FRAME_STYLE : HIDDEN_FRAME_STYLE);
    if (shown) frame.removeAttribute('aria-hidden');
    else frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = shown ? 0 : -1;
}

function mint(host: HTMLElement | null): Promise<{ pass: string; expiresAt: number }> {
    return new Promise((resolve, reject) => {
        const requestId = `rx${Math.random().toString(36).slice(2, 12)}`;
        const frame = document.createElement('iframe');
        frame.title = 'Verification';
        frame.src = `${FRAME_ORIGIN}/?action=checkout&origin=${encodeURIComponent(window.location.origin)}`;
        showFrame(frame, false);

        let settled = false;
        const finish = (error: Error | null, result?: { pass: string; expiresAt: number }) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            frame.remove();
            if (error) reject(error);
            else resolve(result!);
        };
        const startTimer = (ms: number) =>
            window.setTimeout(() => finish(new Error('checkout pass: timed out')), ms);
        let timer = startTimer(PASS_TIMEOUT_MS);

        function onMessage(event: MessageEvent) {
            if (event.origin !== FRAME_ORIGIN || event.source !== frame.contentWindow) return;
            const message = event.data as FrameMessage | null;
            if (!message || message.source !== SOURCE || message.v !== VERSION) return;

            if (message.type === 'ready') {
                frame.contentWindow?.postMessage(
                    { source: SOURCE, v: VERSION, type: 'init', requestId },
                    FRAME_ORIGIN
                );
                return;
            }
            if (message.requestId !== requestId) return;

            if (message.type === 'token' && typeof message.token === 'string' && message.token) {
                void exchange(message.token).then(
                    result => finish(null, result),
                    (error: Error) => finish(error)
                );
            } else if (message.type === 'interactive') {
                if (!host) {
                    // Silent mode: a challenge that wants the visitor is abandoned, never shown.
                    if (message.visible)
                        finish(new Error('checkout pass: interactive challenge declined'));
                    return;
                }
                // Interactive mode: show it, and give the visitor time to answer.
                showFrame(frame, message.visible === true);
                window.clearTimeout(timer);
                timer = startTimer(message.visible ? CHALLENGE_TIMEOUT_MS : PASS_TIMEOUT_MS);
            } else if (
                message.type === 'error' ||
                message.type === 'expired' ||
                message.type === 'timeout' ||
                message.type === 'unsupported'
            ) {
                finish(new Error(`checkout pass: ${String(message.type)}`));
            }
        }

        window.addEventListener('message', onMessage);
        // Moving an iframe reloads it, so an interactive frame starts in its host.
        (host ?? document.body).appendChild(frame);
    });
}

async function exchange(token: string): Promise<{ pass: string; expiresAt: number }> {
    const response = await Api.post<{ token: string }, CheckoutSession>(
        CHECKOUT_SESSION_PATH,
        { token },
        true,
        { headers: brandHostHeaders() }
    );
    const pass = response.ok ? response.data?.pass : undefined;
    if (!pass) throw new Error(`checkout pass: session answered ${response.status}`);
    const at = response.data?.expiresAt ? Date.parse(response.data.expiresAt) : NaN;
    return {
        pass,
        expiresAt: Number.isFinite(at) ? at : Date.now() + (response.data?.expiresIn ?? 3600) * 1000
    };
}
